import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, animate } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  Truck,
  Package,
  Shield,
  Globe,
  Headphones,
  Smartphone,
  CreditCard,
  CheckCircle2,
  Zap,
} from "lucide-react";
import { useI18n } from "../i18n-context";
import { HERITAGE_BACKDROPS, LOGO_VERTICAL, FLEET_DEPOT, VAN_BAGHDAD_SUNNY, VAN_BAGHDAD_RAIN, LOGO_CIHAN_BANK, LOGO_QI, LOGO_RAMASAT, LOGO_ELIXR, LOGO_TAIF, LOGO_SUPERCELL, LOGO_ASTICO, LOGO_RTB } from "../assets";

// ----------------------------- ANIMATED COUNTER -----------------------------
function Counter({ to, suffix = "", prefix = "", duration = 1.6 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration,
      ease: "easeOut",
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to, duration]);

  const display = to >= 1000 ? Math.round(val).toLocaleString("en-US") : val.toFixed(to % 1 === 0 ? 0 : 1);
  return (
    <span ref={ref}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}

// ----------------------------- HERO -----------------------------
function Hero() {
  const { t } = useI18n();
  const [bg, setBg] = useState(0);

  // Rotate the Iraqi heritage backdrops behind the logo
  useEffect(() => {
    const timer = setInterval(() => setBg((i) => (i + 1) % HERITAGE_BACKDROPS.length), 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden bg-white">
      {/* ROTATING HERITAGE BACKDROPS — large, clearly visible, cross-fading */}
      <div className="absolute inset-0 overflow-hidden">
        {HERITAGE_BACKDROPS.map((src, i) => (
          <motion.img
            key={src}
            src={src}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover"
            initial={false}
            animate={{
              opacity: bg === i ? 1 : 0,
              scale: bg === i ? 1.06 : 1,
            }}
            transition={{ opacity: { duration: 1.4 }, scale: { duration: 7, ease: "linear" } }}
          />
        ))}
        {/* Soft readability wash — keeps images vivid, ensures text legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-brand-ink/70 via-brand-ink/45 to-brand-ink/20" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-brand-ink/40" />
      </div>

      {/* ambient glows (subtle, on top of image for depth) */}
      <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-brand-blue/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 h-[460px] w-[460px] rounded-full bg-brand-orange/20 blur-3xl pointer-events-none" />

      <div className="container relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* TEXT */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="lg:col-span-7"
        >
          <div className="inline-flex items-center gap-3 mb-6 font-display font-extrabold uppercase tracking-[0.08em] text-white text-lg md:text-xl lg:text-2xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]" data-testid="hero-kicker">
            <span className="h-2.5 w-2.5 rounded-full bg-brand-orange animate-pulse-soft" />
            {t.hero.kicker}
          </div>
          <h1 className="headline text-5xl sm:text-6xl lg:text-7xl text-white mb-6" data-testid="hero-title">
            {t.hero.title[0]}
            <br />
            <span className="text-brand-orange">{t.hero.title[1]}</span>{" "}
            <span className="relative inline-block">
              {t.hero.title[2]}
              <svg className="absolute -bottom-3 left-0 w-full" viewBox="0 0 300 12" fill="none">
                <motion.path
                  d="M2 9 C 60 2, 180 14, 298 4"
                  stroke="#FF5A1F"
                  strokeWidth="3"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1.2, delay: 0.5, ease: "easeOut" }}
                />
              </svg>
            </span>
          </h1>
          <p className="text-lg md:text-xl text-white/75 max-w-2xl leading-relaxed mb-10" data-testid="hero-subtitle">
            {t.hero.subtitle}
          </p>

          <div className="flex flex-wrap gap-4 mb-12">
            <Link to="/apps" className="btn-primary group" data-testid="hero-cta-primary">
              {t.hero.ctaPrimary}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 flip-x" />
            </Link>
            <Link
              to="/partners"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-white/50 text-white font-semibold text-sm transition-all hover:bg-white hover:text-brand-ink"
              data-testid="hero-cta-secondary"
            >
              {t.hero.ctaSecondary}
            </Link>
          </div>

          {/* stats inline */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6" data-testid="hero-stats">
            <div className="border-t-2 border-white/20 pt-3">
              <div className="text-3xl md:text-4xl font-display font-extrabold text-white">
                <Counter to={2.7} suffix="M+" />
              </div>
              <div className="text-xs md:text-sm text-white/65 mt-1 leading-snug">{t.hero.stat1}</div>
            </div>
            <div className="border-t-2 border-white/20 pt-3">
              <div className="text-3xl md:text-4xl font-display font-extrabold text-white">
                &lt;<Counter to={18} suffix="h" />
              </div>
              <div className="text-xs md:text-sm text-white/65 mt-1 leading-snug">{t.hero.stat2}</div>
            </div>
            <div className="border-t-2 border-white/20 pt-3">
              <div className="text-3xl md:text-4xl font-display font-extrabold text-white">
                <Counter to={18} />
              </div>
              <div className="text-xs md:text-sm text-white/65 mt-1 leading-snug">{t.hero.stat3}</div>
            </div>
            <div className="border-t-2 border-white/20 pt-3">
              <div className="text-3xl md:text-4xl font-display font-extrabold text-white">24/7</div>
              <div className="text-xs md:text-sm text-white/65 mt-1 leading-snug">{t.hero.stat4}</div>
            </div>
          </div>
        </motion.div>

        {/* AKWAAN LOGO — focal piece (only the company logo, no extra badges) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: "easeOut", delay: 0.1 }}
          className="lg:col-span-5 relative flex items-center justify-center"
        >
          {/* soft glow behind the logo */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-[340px] w-[340px] md:h-[420px] md:w-[420px] rounded-full bg-gradient-to-br from-brand-blue/40 to-brand-orange/40 blur-3xl" />
          </div>
          {/* pulsing rings */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="h-[300px] w-[300px] md:h-[380px] md:w-[380px] rounded-full border border-white/15 animate-pulse-soft" />
            <div className="absolute h-[360px] w-[360px] md:h-[460px] md:w-[460px] rounded-full border border-white/10" />
          </div>
          <motion.img
            src={LOGO_VERTICAL}
            alt="Akwaan Express"
            data-testid="hero-logo"
            className="relative z-10 w-64 md:w-80 lg:w-[360px] h-auto drop-shadow-[0_0_40px_rgba(26,79,255,0.35)]"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>
      </div>

      {/* backdrop progress dots (subtle, bottom-center) */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20" data-testid="hero-backdrop-dots">
        {HERITAGE_BACKDROPS.map((_, i) => (
          <button
            key={i}
            onClick={() => setBg(i)}
            aria-label={`backdrop ${i + 1}`}
            className={`h-1.5 transition-all rounded-full ${
              bg === i ? "w-8 bg-brand-orange" : "w-1.5 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>
    </section>
  );
}

// ----------------------------- MARQUEE -----------------------------
function Marquee() {
  const { t } = useI18n();
  const items = [...t.marquee, ...t.marquee, ...t.marquee];
  return (
    <div className="relative bg-brand-ink text-white py-6 overflow-hidden border-y-4 border-brand-orange">
      <div className="marquee-mask">
        <div className="flex gap-12 animate-marquee whitespace-nowrap">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-3 text-2xl md:text-3xl font-display font-extrabold uppercase tracking-tight">
              <Sparkles className="h-5 w-5 text-brand-orange" />
              {it}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ----------------------------- FLEET SHOWCASE -----------------------------
function FleetShowcase() {
  const { t, lang } = useI18n();
  const photos = [
    {
      src: FLEET_DEPOT,
      label: lang === "ar" ? "أسطولنا الكامل" : "Our full fleet",
      tag: lang === "ar" ? "شاحنات للنقل بين الفروع وتسليم داخل المدن" : "Trucks for inter-branch transport & in-city delivery",
    },
    {
      src: VAN_BAGHDAD_SUNNY,
      label: lang === "ar" ? "في قلب بغداد" : "Across central Baghdad",
      tag: lang === "ar" ? "تشغيل يومي" : "Daily operations",
    },
    {
      src: VAN_BAGHDAD_RAIN,
      label: lang === "ar" ? "في كل الظروف" : "In every condition",
      tag: lang === "ar" ? "موثوقية مطلقة" : "Absolute reliability",
    },
  ];

  return (
    <section className="section-pad relative">
      <div className="container">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="kicker mb-4">
              <span className="kicker-dot" />
              {lang === "ar" ? "أسطولنا" : "Our fleet"}
            </div>
            <h2 className="headline text-4xl md:text-5xl text-brand-ink">
              {lang === "ar"
                ? "أسطول مُجهَّز يجوب شوارع العراق كل يوم"
                : "A purpose-built fleet on the road every single day."}
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-lg text-brand-ink/65 leading-relaxed">
              {lang === "ar"
                ? "شاحنات للنقل بين الفروع وتسليم داخل المدن، ودراجات سريعة للتسليم اللحظي — جميعها تحمل هوية أكوان لتعرفها من أول نظرة."
                : "Long-haul trucks between hubs and in-city delivery, plus fast scooters for instant drop-offs — all branded so you spot us at first sight."}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {photos.map((p, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              className="group relative rounded-3xl overflow-hidden bg-brand-ink aspect-[4/5]"
              data-testid={`fleet-photo-${i}`}
            >
              <img
                src={p.src}
                alt={p.label}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ----------------------------- HOW IT WORKS -----------------------------
function HowItWorks() {
  const { lang } = useI18n();
  const steps = [
    {
      icon: CreditCard,
      n: "01",
      t: lang === "ar" ? "الجهة المتعاقدة تنشئ الطلب" : "Partner creates the order",
      d: lang === "ar"
        ? "بنك أو شركة ترفع طلب توصيل عبر تطبيق التاجر، فردياً أو دفعة كبيرة عبر Excel."
        : "A bank or business creates the order via the merchant app — single or bulk via Excel.",
    },
    {
      icon: Package,
      n: "02",
      t: lang === "ar" ? "نستلم الشحنة من موقعكم" : "We pick up from your branch",
      d: lang === "ar"
        ? "فريقنا يستلم البطاقات والطرود أو المستندات من موقعكم وفق جدول مرن متفق عليه."
        : "Our field team picks up cards, parcels or documents from your location on a flexible schedule.",
    },
    {
      icon: Zap,
      n: "03",
      t: lang === "ar" ? "تسليم بـ OTP في أقل من 18 ساعة" : "OTP-verified delivery in <18h",
      d: lang === "ar"
        ? "المندوب يسلّم العميل ويتحقق برمز OTP، وحالة الطلب تتحدّث لحظياً على لوحة التحكم."
        : "Driver hands over to the customer with OTP verification, status updates live on your dashboard.",
    },
    {
      icon: CheckCircle2,
      n: "04",
      t: lang === "ar" ? "تقارير وأرشفة آمنة" : "Reports & secure archive",
      d: lang === "ar"
        ? "كل التسليمات مؤرشفة إلكترونياً مع إمكانية تصدير التقارير بصيغة Excel."
        : "Every delivery is electronically archived; export full reports in Excel any time.",
    },
  ];

  return (
    <section className="section-pad bg-brand-paper relative overflow-hidden">
      <div className="absolute inset-0 bg-grid pointer-events-none opacity-40" />
      <div className="container relative">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="kicker justify-center mb-4">
            <span className="kicker-dot" />
            {lang === "ar" ? "كيف نعمل" : "How it works"}
          </div>
          <h2 className="headline text-4xl md:text-5xl text-brand-ink mb-5">
            {lang === "ar" ? "أربع خطوات. زمن محدد. صفر مفاجآت." : "Four steps. Defined SLA. Zero surprises."}
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="relative bg-white rounded-3xl border border-brand-ink/10 p-7 hover:shadow-xl hover:-translate-y-1 transition-all"
                data-testid={`step-card-${i}`}
              >
                <div className="absolute top-4 right-5 font-display font-extrabold text-5xl text-brand-blue/10">
                  {s.n}
                </div>
                <div className="h-12 w-12 rounded-xl bg-brand-blue text-white flex items-center justify-center mb-5">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="font-display font-bold text-lg text-brand-ink mb-2 leading-snug">{s.t}</div>
                <p className="text-sm text-brand-ink/60 leading-relaxed">{s.d}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ----------------------------- WHY AKWAAN -----------------------------
function WhyAkwaan() {
  const { lang } = useI18n();
  const features = [
    {
      icon: Shield,
      t: lang === "ar" ? "حماية على مستوى البنوك" : "Bank-grade security",
      d: lang === "ar" ? "OTP لكل تسليم، فحص هوية، أرشفة مشفّرة." : "OTP per delivery, ID checks, encrypted archive.",
    },
    {
      icon: Zap,
      t: lang === "ar" ? "أقل من 18 ساعة" : "Under 18 hours",
      d: lang === "ar" ? "بدلاً من 72 ساعة في الخدمات التقليدية." : "vs. 72h in traditional services.",
    },
    {
      icon: Globe,
      t: lang === "ar" ? "تغطية كاملة" : "Full coverage",
      d: lang === "ar" ? "18 محافظة تشمل المراكز والأطراف." : "18 governorates, centers and outskirts.",
    },
    {
      icon: Headphones,
      t: lang === "ar" ? "دعم 24/7" : "24/7 support",
      d: lang === "ar" ? "مدير حساب مخصّص + دعم فني وميداني." : "Dedicated account manager + tech & field ops.",
    },
  ];

  return (
    <section className="section-pad bg-brand-ink text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" />
      <div className="absolute -top-32 right-0 h-[500px] w-[500px] rounded-full bg-brand-blue/20 blur-3xl pointer-events-none" />
      <div className="container relative grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <div className="kicker !text-brand-orange mb-4">
            <span className="kicker-dot !bg-brand-blue" />
            {lang === "ar" ? "لماذا أكوان" : "Why Akwaan"}
          </div>
          <h2 className="headline text-4xl md:text-5xl mb-6">
            {lang === "ar"
              ? "نحن لا نوصل طروداً فقط، نحمي ثقة عملائك."
              : "We don't just move parcels — we protect your customer trust."}
          </h2>
          <p className="text-white/70 text-lg leading-relaxed mb-8">
            {lang === "ar"
              ? "كل بطاقة دفع وكل مستند مالي تستلمه أكوان يمر عبر دورة تشغيلية ذكية تضمن السرعة، الأمان، والشفافية الكاملة لشريكنا."
              : "Every payment card and financial document we handle goes through an intelligent operational loop that guarantees speed, safety, and full transparency for our partners."}
          </p>
          <Link to="/about" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-orange text-white font-semibold hover:bg-brand-orangeDark transition-colors">
            {lang === "ar" ? "اعرف المزيد عنّا" : "Learn more about us"}
            <ArrowRight className="h-4 w-4 flip-x" />
          </Link>
        </div>

        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-2xl bg-white/5 border border-white/10 p-6 hover:bg-white/10 hover:border-brand-orange transition-all"
              >
                <div className="h-11 w-11 rounded-xl bg-brand-orange flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <div className="font-display font-bold text-lg mb-1">{f.t}</div>
                <p className="text-sm text-white/60 leading-relaxed">{f.d}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ----------------------------- TEASERS -----------------------------
function Teasers() {
  const { t, lang } = useI18n();
  const teasers = [
    { id: "about", icon: Sparkles, t: t.nav.about, d: t.about.lead, path: "/about" },
    { id: "services", icon: Package, t: t.nav.services, d: t.services.lead, path: "/services" },
    { id: "apps", icon: Smartphone, t: t.nav.apps, d: t.apps.lead, path: "/apps" },
    { id: "partners", icon: Shield, t: t.nav.partners, d: t.partners.lead, path: "/partners" },
    { id: "faq", icon: Headphones, t: t.nav.faq, d: lang === "ar" ? "إجابات على ما يخطر ببالك حول خدماتنا." : "Answers to what's likely on your mind.", path: "/faq" },
    { id: "contact", icon: Globe, t: t.nav.contact, d: t.contact.lead, path: "/contact" },
  ];

  return (
    <section className="section-pad">
      <div className="container">
        <div className="max-w-3xl mb-14">
          <div className="kicker mb-4">
            <span className="kicker-dot" />
            {lang === "ar" ? "اكتشف أكوان" : "Explore Akwaan"}
          </div>
          <h2 className="headline text-4xl md:text-5xl text-brand-ink">
            {lang === "ar"
              ? "كل ما تحتاج معرفته، في أقسام منظّمة"
              : "Everything you need, in clean dedicated pages."}
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {teasers.map((te, i) => {
            const Icon = te.icon;
            return (
              <motion.div
                key={te.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
              >
                <Link
                  to={te.path}
                  className="card-tilt group rounded-3xl bg-white border border-brand-ink/10 p-7 flex flex-col gap-5 h-full"
                  data-testid={`home-teaser-${te.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="h-12 w-12 rounded-xl bg-brand-blue/10 flex items-center justify-center group-hover:bg-brand-blue group-hover:text-white transition-colors">
                      <Icon className="h-6 w-6 text-brand-blue group-hover:text-white" />
                    </div>
                    <span className="font-mono text-xs text-brand-ink/40">0{i + 1}</span>
                  </div>
                  <div>
                    <div className="font-display font-bold text-xl text-brand-ink mb-2">{te.t}</div>
                    <p className="text-sm text-brand-ink/60 leading-relaxed line-clamp-3">{te.d}</p>
                  </div>
                  <div className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-brand-orange">
                    {lang === "ar" ? "تصفّح القسم" : "Visit page"}
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ----------------------------- PARTNERS / TRUST STRIP -----------------------------
function TrustStrip() {
  const { t, lang } = useI18n();

  const partners = [
    {
      logo: LOGO_CIHAN_BANK,
      name: "Cihan Bank",
      ar: "بنك جيهان",
      url: "https://www.cihanbank.com.iq/",
      industry: t.trust.industries.bank,
    },
    {
      logo: LOGO_QI,
      name: "Qi card",
      ar: "كي كارد",
      url: "https://qi.iq/en/home",
      industry: t.trust.industries.payments,
      dark: true,
    },
    {
      logo: LOGO_ELIXR,
      name: "Elixr",
      ar: "إليكسر",
      url: "https://www.instagram.com/elixr.iq/",
      industry: t.trust.industries.retail,
      cropBorders: true,
    },
    {
      logo: LOGO_TAIF,
      name: "Taif Al Emarat",
      ar: "طيف الإمارات",
      url: "https://sa.taifalemarat.com/",
      industry: t.trust.industries.luxury,
    },
    {
      logo: LOGO_RAMASAT,
      name: "Ramasat",
      ar: "رمسات",
      url: "https://ramasat.com/en",
      industry: t.trust.industries.media,
    },
    {
      logo: LOGO_SUPERCELL,
      name: "Supercell",
      ar: "سوبر سيل",
      url: "#",
      industry: t.trust.industries.internet,
    },
    {
      logo: LOGO_ASTICO,
      name: "Astico Store",
      ar: "أستيكو ستور",
      url: "#",
      industry: t.trust.industries.ecommerce,
    },
    {
      logo: LOGO_RTB,
      name: "RTB",
      ar: "آر تي بي",
      url: "#",
      industry: t.trust.industries.digital,
    },
  ];

  return (
    <section className="section-pad bg-white relative overflow-hidden border-y border-brand-ink/5">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="kicker justify-center mb-4">
            <span className="kicker-dot" />
            {t.trust.kicker}
          </div>
          <h2 className="headline text-4xl md:text-5xl text-brand-ink mb-5" data-testid="partners-title">
            {t.trust.title}
          </h2>
          <p className="text-base md:text-lg text-brand-ink/65 leading-relaxed">{t.trust.lead}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 md:gap-12 items-center">
          {partners.map((p, i) => (
            <motion.a
              key={p.name}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              data-testid={`partner-card-${i}`}
              className="group flex flex-col items-center justify-center text-center"
            >
              <div
                className={`w-full h-24 md:h-28 flex items-center justify-center overflow-hidden transition-transform duration-500 group-hover:scale-105 ${
                  p.cropBorders ? "rounded-full" : ""
                }`}
              >
                <img
                  src={p.logo}
                  alt={p.name}
                  className={`w-auto object-contain ${
                    p.cropBorders ? "max-h-36 md:max-h-40 scale-[1.35]" : "max-h-20 md:max-h-24"
                  }`}
                  loading="lazy"
                />
              </div>
              <div className="mt-4 font-display font-bold text-sm md:text-base leading-tight text-brand-ink/80 group-hover:text-brand-ink transition-colors">
                {lang === "ar" ? p.ar : p.name}
              </div>
            </motion.a>
          ))}
        </div>

        <div className="mt-10 flex items-center justify-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-brand-ink/40">
          <span className="h-px w-10 bg-brand-ink/20" />
          {lang === "ar" ? "وآخرون" : "and more"}
          <span className="h-px w-10 bg-brand-ink/20" />
        </div>
      </div>
    </section>
  );
}

// ----------------------------- FINAL CTA -----------------------------
function FinalCTA() {
  const { t, lang } = useI18n();
  return (
    <section className="section-pad bg-brand-blue text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-dark opacity-25 pointer-events-none" />
      <div className="absolute top-0 -left-10 h-[500px] w-[500px] rounded-full bg-brand-orange/30 blur-3xl pointer-events-none" />
      <div className="container relative grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="kicker !text-white/80 mb-4">
            <span className="kicker-dot !bg-brand-orange" />
            {t.partners.kicker}
          </div>
          <h2 className="headline text-4xl md:text-5xl mb-5">{t.partners.title}</h2>
          <p className="text-lg text-white/80 leading-relaxed">{t.partners.lead}</p>
        </div>
        <div className="flex flex-wrap gap-4 lg:justify-end">
          <Link to="/partners" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-brand-orange text-white font-bold text-base hover:bg-brand-orangeDark transition-colors" data-testid="home-cta-partners">
            {t.partners.form.submit}
            <ArrowRight className="h-4 w-4 flip-x" />
          </Link>
          <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white text-brand-ink font-bold text-base hover:bg-brand-paper transition-colors" data-testid="home-cta-contact">
            {t.nav.contact}
          </Link>
        </div>
      </div>
    </section>
  );
}

// ----------------------------- ROOT -----------------------------
export default function HomePage() {
  return (
    <>
      <Hero />
      <Marquee />
      <FleetShowcase />
      <HowItWorks />
      <WhyAkwaan />
      <Teasers />
      <TrustStrip />
      <FinalCTA />
    </>
  );
}
